import React, { useCallback, useState } from 'react';
import { 
  Type, Hash, CheckSquare, SquareStack, Box, CircleDot, Plus, Trash2, Shield
} from 'lucide-react';
import clsx from 'clsx';
import { getTypeColorClass } from '../../utils/schemaUtils';
import { Priority, Parameter } from '../../types/schema';
import EventUsageBar from './EventUsageBar';

interface ParameterTreeViewProps {
  param: Parameter;
  isEditable: boolean;
  totalEventCount: number;
  usedInEventCount: number;
  eventNames: string[]; // New prop to list event names
  startEditing: () => void;
  onAddParameter: () => void;
  onDeleteParameter: () => void;
  showDeleteConfirmation: boolean; // To keep action buttons visible during confirmation
  hidePriorityPill: boolean; // New prop
  hideRegex: boolean; // New prop
}

const ParameterTreeView: React.FC<ParameterTreeViewProps> = ({
  param,
  isEditable,
  totalEventCount,
  usedInEventCount,
  eventNames, // Destructure eventNames
  startEditing,
  onAddParameter,
  onDeleteParameter,
  showDeleteConfirmation,
  hidePriorityPill,
  hideRegex,
}) => {
  const [isHovered, setIsHovered] = useState(false); // Added local isHovered state
  // Get the appropriate icon for the data type
  const getTypeIcon = useCallback(() => {
    switch (param.dataTypes[0]) {
      case 'string':
        return <Type size={16} />;
      case 'number':
        return <Hash size={16} />;
      case 'boolean':
        return <CheckSquare size={16} />;
      case 'array':
        return <SquareStack size={16} />;
      case 'object':
        return <Box size={16} />;
      case 'null':
        return <CircleDot size={16} />;
      default:
        return <Type size={16} />;
    }
  }, [param.dataTypes]);

  const getPriorityColorClass = (priority: Priority) => {
    switch (priority) {
      case 'Required': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'Standard': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'Optional': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <div 
      className={clsx(
        "flex items-center min-h-[45px] w-full rounded-lg cursor-pointer px-1 border-[1px] border-solid",
        !isHovered ? "border-transparent" : "border-blue-200 dark:border-blue-700",
        isHovered && "bg-blue-50 dark:bg-blue-900/30 shadow",
      )}
      onClick={startEditing}
      onMouseEnter={() => setIsHovered(true)} 
      onMouseLeave={() => setIsHovered(false)} 
    >
      {/* Main content with fixed width to ensure consistent layout */}
      <div className="flex items-center flex-wrap gap-x-2 gap-y-1 w-[calc(100%-160px)] min-w-0">
        {/* Only show detailed info if it's an actual data property */}
        {param.dataTypes && param.dataTypes.length > 0 ? (
          <>
            {/* Data Type */}
            <span className={`inline-flex w-6 h-6 justify-center items-center rounded-full ${getTypeColorClass(param.dataTypes[0])}`}>
              {getTypeIcon()}
            </span>

            {/* Friendly Name */}
            <span className="font-semibold text-[15px] text-gray-900 dark:text-white">
              {param.friendlyName || <span className="italic text-gray-400">Unnamed Parameter</span>}
            </span>

            {/* Code Name */}
            {param.codeName && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium font-mono border border-gray-300 dark:border-gray-600 dark:bg-gray-800 text-gray-800 dark:text-gray-300">
                {param.codeName}
              </span>
            )}

            {/* PII Label */}
            {param.isPii && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900 dark:text-fuchsia-200">
                <Shield size={12} className="mr-1" /> PII
              </span>
            )}

            {/* Priority */}
            {param.priorities && param.priorities.length > 0 && !hidePriorityPill && param.priorities[0] !== 'Standard' && (
              <span className={clsx(
                "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                getPriorityColorClass(param.priorities[0])
              )}>
                {param.priorities[0]}
              </span>
            )}

            {/* Description (truncated) */}
            {param.description && (
              <span className="text-gray-700 dark:text-gray-300 text-xs truncate max-w-[200px]" title={param.description}>
                {param.description.length > 50 ? `${param.description.substring(0, 50)}...` : param.description}
              </span>
            )}

            {/* Regex Formula */}
            {param.regexFormula && !hideRegex && (
              <span className="text-gray-700 dark:text-gray-300 text-xs truncate max-w-[150px]" title={param.regexFormula}>
                Regex: {param.regexFormula}
              </span>
            )}
          </>
        ) : (
          // For structural nodes, just show friendly name or code name
          <span className="font-semibold text-gray-900 dark:text-white">
            {param.friendlyName || param.codeName || <span className="italic text-gray-400">Unnamed Group</span>}
          </span>
        )}
      </div>
      
      {/* Used in X of Y events - Fixed width */}
      <div className="w-32 text-right flex-shrink-0">
        <EventUsageBar usedCount={usedInEventCount} totalCount={totalEventCount} eventNames={eventNames} />
      </div>

      {/* Action buttons (visible on hover) - Fixed width */}
      <div className={clsx(
        `flex items-center gap-1 w-24 justify-end flex-shrink-0`,
        isHovered || showDeleteConfirmation ? 'opacity-100' : 'opacity-0'
      )}>
        {isEditable && (
          <>
            {(param.dataTypes.includes('array') || param.dataTypes.includes('object')) && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('Add Sub-Property button clicked');
                  onAddParameter();
                }}
                className="p-2 rounded-md text-gray-600 hover:text-blue-600 hover:bg-blue-100 dark:text-gray-300 dark:hover:bg-blue-900/50 dark:hover:text-blue-400"
                title="Add Sub-Property"
              >
                <Plus size={20} /> {/* Increased size */}
              </button>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteParameter();
              }}
              className="p-2 rounded-md text-gray-600 hover:text-red-600 hover:bg-red-100 dark:text-gray-300 dark:hover:bg-red-900/50 dark:hover:text-red-400"
              title="Delete Parameter"
            >
              <Trash2 size={20} /> {/* Increased size */}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ParameterTreeView;
